import { useAuth } from '@/store/auth';
import metadata from 'libphonenumber-js/metadata.min.json';
import { useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useNavigate } from 'react-router-dom';
import { validatePhoneNumber } from '../../helper/phoneUtils';
import { authService } from '../../services/authService/authService';
import CustomDropdown from '../CommonComponents/CustomDropdown';

export const COUNTRY_CODES: string[] = Array.from(
  new Set(
    Object.values(metadata.countries).map((country: any) => country[0]), // calling code
  ),
)
  .map((code) => `+${code}`)
  .sort((a, b) => a.length - b.length || a.localeCompare(b));

const options = COUNTRY_CODES.map((code) => ({ value: code, label: code }));

export default function LoginOTP() {
  const nav = useNavigate();
  const email = useAuth((s) => s.email);
  const phone = useAuth((s) => s.phone);
  const setEmail = useAuth((s) => s.setEmail);
  const setPhone = useAuth((s) => s.setPhone);
  const setVerifiedUser = useAuth((s) => s.setVerifiedUser);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  useEffect(() => {
    localStorage.clear();
  }, []);

  const [countryCode, setCountryCode] = useState('+91');
  const [errors, setErrors] = useState<{ email?: string; phone?: string; recaptcha?: string }>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [userName, setUserName] = useState<String | null>(null);

  const handleRecaptchaChange = (token: string | null) => {
    setRecaptchaToken(token);
    if (token) {
      setErrors((prev) => ({ ...prev, recaptcha: undefined }));
    }
  };

  async function handleSendOtp(e: React.FormEvent) {
    e.preventDefault();
    const errs: typeof errors = {};

    const validation = validatePhoneNumber(phone, countryCode);
    if (!validation.isValid) {
      errs.phone = validation.message || 'Invalid phone number';
    }

    if (!recaptchaToken) {
      errs.recaptcha = 'Please verify that you are not a robot';
    }

    setErrors(errs);

    if (Object.keys(errs).length === 0) {
      setLoading(true);
      setApiError(null);
      try {
        const finalPhone = countryCode + phone;

        const verifyResult = await authService.verifyUser(countryCode, phone);

        if (!verifyResult.success) {
          throw new Error('User not verified from API');
        }

        setVerifiedUser(verifyResult.data);

        const result = await authService.sendOTP(finalPhone, recaptchaToken!);
        if (result.alreadySignedIn) {
          setPhone('');
          setEmail('');
          nav('/dashboard');
        } else {
          sessionStorage.setItem('otpTimerExpiry', String(Date.now() + 120_000));
          nav('/verify', { state: { phone: finalPhone, email, username: result.username } });
        }
      } catch (error: any) {
        setApiError(error.message);
        recaptchaRef.current?.reset();
        setRecaptchaToken(null);
      } finally {
        setLoading(false);
      }
    }
  }

  return (
    <div className="min-h-screen w-full flex font-lato">
      {/* Left Section - Branding */}
      <div className="w-[30%] bg-gradient-to-b from-[#AD0C62] to-[#621C43] text-white flex flex-col justify-between">
        <div className="flex justify-between border-b border-[#FFF8ED] p-4">
          <h1 className="text-3xl font-bold">
            MillionMakers™ <br /> Admin Portal
          </h1>
          <img src="/images/logo.png" className="h-16" alt="logo" />
        </div>
        <div className="p-4">
          <p className="text-2xl leading-relaxed ">You can login using a phone number</p>
        </div>
      </div>

      {/* Right Section - Form */}
      <div className="w-[70%] bg-[#bc92a840] text-[#A40E5F] flex flex-col">
        <div className="">
          {/* Title */}
          <h2 className=" p-10 text-4xl font-semibold border-b border-[#A40E5F] pb-6">
            Login with Phone Number
          </h2>
          <p className="p-6 text-2xl text-[#661B44]">
            Please enter your phone number to get access to your account and access your
            personalized dashboard.
          </p>

          {/* Form */}
          <form onSubmit={handleSendOtp} className=" p-5 flex flex-col gap-8 ">
            {/* Phone Input */}
            <div>
              <div className="flex items-center text-2xl gap-3">
                <div className="flex justify-between px-[20px] w-[90px] mb-[8px] mr-3 pb-1 border-b-2 border-[#A40E5F] text-[#A40E5F] py-1 mt-[14px] placeholder:text-2xl">
                  <CustomDropdown
                    value={countryCode}
                    onChange={(value) => setCountryCode(value.toString())}
                    options={options}
                    placeholder="Select country"
                    overrideClassName="flex  justify-between bg-transparent pr-[20px] text-2xl text-[#A40E5F]"
                  />
                </div>

                <input
                  type="tel"
                  inputMode="numeric"
                  placeholder="Enter Phone Number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="flex-1 bg-transparent border-b-2 border-[#A40E5F] placeholder-[#A40E5F] text-[#A40E5F] pl-1 pr-3 py-2 outline-none"
                />
              </div>
              {errors.phone && <p className="text-red-500 text-sm">{errors.phone}</p>}
              {apiError && <p className="text-red-500 text-sm mt-1">{apiError}</p>}
            </div>
            {/* reCAPTCHA */}
            <div>
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY!}
                onChange={handleRecaptchaChange}
              />
              {errors.recaptcha && <p className="text-red-500 text-sm mt-2">{errors.recaptcha}</p>}
            </div>
            {/* Buttons */}
            <div className="flex gap-4 pb-10">
              <button
                type="submit"
                disabled={loading}
                className="absolute bottom-10 p-5 right-5 flex-1 py-3 rounded bg-[#EA600E] text-white hover:bg-orange-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Sending...' : 'Receive Login Code'}
              </button>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="mt-auto flex items-center justify-end p-4">
          <p className="text-xs font-bold text-[#621C43]">Powered by Bluenumber®</p>
        </div>
      </div>
    </div>
  );
}
